import { useCallback } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import type { BaseEntity, SimpleTableConfig } from "../../UniversalCRMTable/types";
import type { GlobalDrawerStack, GlobalModalStack } from "./crudHandlerTypes";

/**
 * Lightweight "open/close modal" trigger handlers used by the UniversalEntityPage.
 *
 * These do NOT perform mutations — they only coordinate which modal/drawer is
 * visible and stash the target item via the supplied setters.
 */
export function useCrudTriggerHandlers<T extends BaseEntity>(
  entityName: string,
  config: SimpleTableConfig<T>,
  isMobile: boolean,
  globalModalStack: GlobalModalStack,
  drawerStack: GlobalDrawerStack,
  setEditingItem: (item: T | null) => void,
  setDeletingItem: (item: T | null) => void,
  canCreate: boolean
) {
  const router = useRouter();

  const handleCreateItem = useCallback(() => {
    if (isMobile) {
      // Use drawer stack for mobile
      drawerStack.openDrawer(`create-${entityName}-drawer`, {
        title: `Create New ${config.displayName}`,
        description: `Create a new ${config.displayName.toLowerCase()}`,
        side: "bottom",
        height: "95vh",
        enableSwipeGesture: true,
        showCloseButton: true,
      });
    } else {
      // Use global modal stack for desktop
      globalModalStack.openModal(`create-${entityName}`);
    }
  }, [isMobile, drawerStack, globalModalStack, entityName, config.displayName]);

  const handleEditItem = useCallback(
    (item: T) => {
      setEditingItem(item);
      globalModalStack.openModal(`edit-${entityName}`);
    },
    [globalModalStack, entityName, setEditingItem]
  );

  const handleDeleteItem = useCallback(
    (item: T) => {
      setDeletingItem(item);
      globalModalStack.openModal(`delete-${entityName}`);
    },
    [globalModalStack, entityName, setDeletingItem]
  );

  const handleViewItem = useCallback(
    (item: T) => {
      // Navigate to the detail page instead of opening a modal
      router.push(`/${entityName}/${item.id}`);
    },
    [router, entityName]
  );

  const handleImportItems = useCallback(() => {
    if (!canCreate) {
      toast.error("You do not have permission to import.");
      return;
    }
    globalModalStack.openModal("csv-import");
  }, [canCreate, globalModalStack]);

  const handleCancelDelete = useCallback(() => {
    setDeletingItem(null);
    globalModalStack.closeModal(`delete-${entityName}`);
  }, [globalModalStack, entityName, setDeletingItem]);

  return {
    handleCreateItem,
    handleEditItem,
    handleDeleteItem,
    handleViewItem,
    handleImportItems,
    handleCancelDelete,
  };
}
